import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "c-dynamic-form__items" }
const _hoisted_2 = {
  key: 0,
  class: "c-dynamic-form__external"
}

import { ref, computed, provide, nextTick } from 'vue';
import {
  DynamicFormContext,
  DynamicFormEmits,
  DynamicFormField,
  DynamicFormFieldRef,
  DynamicFormProps,
  DynamicFormUpdateParam,
  FormFieldTypes,
} from '@/types/form/dynamic-form';
import { ScItem } from '@/types/sitecore';
import { merge } from 'lodash-es';
import utils from '@/utils';
import qs from 'qs';

export default /*@__PURE__*/_defineComponent({
  __name: 'DynamicForm',
  props: {
    form: {},
    showSearchIcon: { type: Boolean }
  },
  emits: ["iconClick", "change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;
const formItems = ref<Record<string, any>>({});
const firstInvalidField = ref<DynamicFormFieldRef | null>(null);
const fields = computed(() => props.form?.fields);
const items = computed(() => fields.value?.items ?? []);
const hasItems = computed(() => items.value.length > 0);
const rootStyle = computed(() => {
  return {
    '--item-back-color': fields.value?.itemBackColor?.value,
    '--item-color': fields.value?.itemForeColor?.value,
    '--item-border-color': fields.value?.itemBorderColor?.value,
    '--item-curtain-back-color': fields.value?.itemCurtainBackColor?.value,
    '--item-curtain-color': fields.value?.itemCurtainColor?.value,
    '--checkbox-back-color': fields.value?.checkboxBackColor?.value,
    '--checkbox-border-color': fields.value?.checkboxBorderColor?.value,
    '--checkbox-color': fields.value?.checkboxForeColor?.value,
    '--checkbox-active-back-color': fields.value?.checkboxActiveBackColor?.value,
    '--checkbox-active-color': fields.value?.checkboxActiveForeColor?.value,
  };
});
// Register form element
const regFormEl = (fieldRef: DynamicFormFieldRef) => {
  if (fieldRef && fieldRef.name) {
    formItems.value[fieldRef.name] = fieldRef;
  }
};

// Delete element registration
const unRegFormEl = (fieldRef: DynamicFormFieldRef) => {
  if (fieldRef && fieldRef.name && formItems.value[fieldRef.name]) {
    delete formItems.value[fieldRef.name];
  }
};

// Handle icon click
const onIconClick = (iconKey: string, value: string) => {
  emit('iconClick', iconKey, value);
};

// Handle dropdown change
const onChange = (fieldType: string, fieldName: string, value: string, originValue?: any) => {
  emit('change', fieldType, fieldName, value, originValue);
};

// Get field by name
const getField = (key: string) => {
  if (formItems.value[key]) return formItems.value[key];
  const normalizedKey = key.toLowerCase();
  const matchedKey = Object.keys(formItems.value).find(
    (itemKey) => itemKey.toLowerCase() === normalizedKey
  );
  return matchedKey ? formItems.value[matchedKey] : null;
};

// Update fields
const updateFields = (updates: DynamicFormUpdateParam) => {
  const keys = Object.keys(updates);
  for (const key of keys) {
    const updateInfo = updates[key];
    const item = getField(key);
    if (!item) continue;

    // A dropdown must receive its options before restoring a selected value.
    if (updateInfo.options !== undefined) {
      if (typeof item.updateOptions === 'function') {
        item.updateOptions(updateInfo.options);
      }
    }
    if (Object.hasOwn(updateInfo, 'value')) {
      if (typeof item.updateValue === 'function') {
        item.updateValue(updateInfo.value);
      }
    }
    if (updateInfo.disabled !== undefined) {
      if (typeof item.setDisabled === 'function') {
        item.setDisabled(updateInfo.disabled);
      }
    }
    if (updateInfo.required !== undefined) {
      if (typeof item.setRequired === 'function') {
        item.setRequired(updateInfo.required);
      }
    }
    if (Object.hasOwn(updateInfo, 'startData')) {
      if (typeof item.setStartData === 'function') {
        item.setStartData(updateInfo.startData);
      }
    }
    if (Object.hasOwn(updateInfo, 'endData')) {
      if (typeof item.setEndData === 'function') {
        item.setEndData(updateInfo.endData);
      }
    }
    if (updateInfo.visible !== undefined) {
      item.visible = updateInfo.visible;
      if (typeof item.setVisible === 'function') {
        item.setVisible(updateInfo.visible);
      }
    }
  }
};

// Reset all fields
const resetFields = () => {
  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (typeof item.updateValue === 'function') {
      item.updateValue(null);
    }
  }
};

// Set all fields disabled state
const setDisabled = (isDisabled: boolean) => {
  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (typeof item.setDisabled === 'function') {
      item.setDisabled(isDisabled);
    }
  }
};

// Validate all fields
const validate = async () => {
  let valid = true;
  firstInvalidField.value = null;
  const errors = [];
  const data: Record<string, any> = {};
  if (fields.value?.externalData?.value) {
    try {
      data.externalData = qs.parse(fields.value.externalData.value);
    } catch (error) {
      console.error('Error parsing externalData:', error);
    }
  }

  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (!item.visible) continue;

    const fieldResult = await item.validate();
    data[key] = fieldResult.value;
    valid = valid && fieldResult.valid;
    if (!fieldResult.valid && !firstInvalidField.value) {
      firstInvalidField.value = item;
    }
    if (fieldResult.message) {
      errors.push(fieldResult.message);
    }
  }

  return {
    valid,
    errors,
    data,
  };
};

const focusFirstInvalid = () => {
  const field = firstInvalidField.value;
  if (!field) return;

  const element = field.el;
  const target = element?.matches?.('input, textarea, select, button, [tabindex]')
    ? element
    : element?.querySelector?.('input, textarea, select, button, [tabindex]');
  const focusTarget = target || element;

  (element || target)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  if (focusTarget instanceof HTMLElement) {
    if (!target) focusTarget.setAttribute('tabindex', '-1');
    nextTick(() => focusTarget.focus({ preventScroll: true }));
  }
};

// Get all form values
const getValues = () => {
  const values = {};
  const keys = Object.keys(formItems.value);
  for (const key of keys) {
    const item = formItems.value[key];
    if (item.visible && typeof item.getValue === 'function') {
      merge(values, {
        [key]: item.getValue(),
      });
    }
  }
  return values;
};

const getComponentType = (item: ScItem<DynamicFormField>) => {
  let fieldType = item?.fields?.fieldType?.fields?.phrase?.value;
  if (!fieldType) return 'FormInput';
  fieldType = utils.string.toPascalCase(fieldType)!;
  if (!FormFieldTypes.includes(fieldType)) return 'FormInput';
  return fieldType;
};

// Provide form context to child components
const formContext: DynamicFormContext = {
  regFormEl,
  unRegFormEl,
};
provide('formContext', formContext);

// Expose methods for parent component
__expose({
  updateFields,
  resetFields,
  setDisabled,
  validate,
  focusFirstInvalid,
  getValues,
  getField,
});

return (_ctx: any,_cache: any) => {
  return (hasItems.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "c-dynamic-form",
        style: _normalizeStyle(rootStyle.value)
      }, [
        _createElementVNode("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, index) => {
            return (_openBlock(), _createBlock(_resolveDynamicComponent(getComponentType(item)), {
              key: `${item.id}-${index}`,
              item: item,
              onIconClick: onIconClick,
              onChange: onChange
            }, null, 40, ["item"]))
          }), 128))
        ]),
        (_ctx.$slots.default)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _renderSlot(_ctx.$slots, "default")
            ]))
          : _createCommentVNode("", true)
      ], 4))
    : _createCommentVNode("", true)
}
}

})